import React from "react";

import LockIcon from "@uui/Lock03";
import MinimizeIcon from "@uui/Minimize01";
import RefreshIcon from "@uui/RefreshCcw01";
import PreviousIcon from "@uui/SkipBack";
import NextIcon from "@uui/SkipForward";
import NoVideoIcon from "@uui/VideoRecorderOff";
import CloseIcon from "@uui/XClose";

import { Box, IconButtonProps } from "@mui/material";
import styled from "@mui/material/styles/styled";

import { PlayFilled, PauseIcon } from "@app/common/assets/icons";
import LINKS from "@app/common/links";

import IconButton from "@rui/IconButton";
import Link from "@rui/Link";
import Stack, { StackProps } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

type PlaybackOverlayButtonIcon =
  | "lock"
  | "no_video"
  | "pause"
  | "play"
  | "replay"
  | "minimize"
  | "previous"
  | "next"
  | "close";

const UpsellTooltipLockIcon = () => (
  <TooltipIcon
    title={
      <>
        <Link
          color="secondary.light"
          underline
          href={LINKS.MARKETING.PLANS_PRICING}
        >
          Upgrade to Read Enterprise
        </Link>{" "}
        to unlock premium features, such as video clips and playback!
      </>
    }
    Icon={LockIcon}
  />
);

const SVG_BY_ICON: { [key in PlaybackOverlayButtonIcon]: typeof LockIcon } = {
  close: CloseIcon,
  lock: UpsellTooltipLockIcon,
  minimize: MinimizeIcon,
  next: NextIcon,
  no_video: NoVideoIcon,
  pause: PauseIcon,
  play: PlayFilled,
  previous: PreviousIcon,
  replay: RefreshIcon,
};

type LoadWrapperProps = StackProps & { duration: number };

export const LoadWrapper = styled<React.FC<LoadWrapperProps>>(
  ({ children, ...props }) => (
    <Stack {...props}>
      <Box className="load--fill" />
      <Box className="load--mask" />
      <Stack variant="center" height="100%" width="100%" sx={{ zIndex: 5 }}>
        {children}
      </Stack>
    </Stack>
  )
)(({ duration, theme }) => ({
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  "@keyframes fill": {
    "0%": { opacity: 0 },
    "50%, 100%": { opacity: 1 },
  },
  "@keyframes mask": {
    "0%": { opacity: 1 },
    "50%, 100%": { opacity: 0 },
  },
  background: "#FFF",
  borderRadius: "50%",
  fontSize: "48px",
  height: "1em",
  width: "1em",
  overflow: "hidden",
  position: "relative",
  "&::before, &::after": {
    boxSizing: "border-box",
    background: theme.palette.shades.primary[500],
    content: "''",
    height: "1em",
    display: "block",
    position: "absolute",
    width: "0.5em",
  },

  "&::before": {
    animation: `spin ${duration}s linear infinite`,
    borderRadius: "999px 0 0 999px",
    transformOrigin: "0.5em 0.5em",
  },
  "&::after": {
    animation: `fill ${duration}s steps(1, end) infinite`,
    borderRadius: "0 999px 999px 0",
    opacity: 0,
    right: 0,
    top: 0,
  },
  "& .load--fill": {
    background: "#000",
    borderRadius: "50%",
    height: "40px",
    width: "40px",
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translate3d(-50%, -50%, 0)",
    zIndex: 3,
  },
  "& .load--mask": {
    animation: `mask ${duration}s steps(1, end) infinite`,
    background: "inherit",
    borderRadius: "999px 0 0 999px",
    height: "1em",
    position: "absolute",
    width: "0.5em",
  },
}));

type ButtonProps = Pick<
  IconButtonProps,
  "disabled" | "onClick" | "children" | "className" | "sx"
>;

const StyledButton = styled<React.FC<ButtonProps>>(
  ({ disabled, onClick, children, ...props }) => (
    <Stack {...props}>
      <IconButton
        className="playback-overlay-button__button"
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </IconButton>
    </Stack>
  )
)(({ theme }) => ({
  "& .playback-overlay-button__button": {
    backgroundColor: theme.palette.transparent.white[30],
    borderRadius: "50%",
    "&:hover:not(:disabled)": {
      backgroundColor: theme.palette.transparent.white[50],
    },
    "&:disabled": {
      backgroundColor: theme.palette.transparent.white[30],
      "& svg": {
        color: theme.palette.transparent.white[40],
      },
    },
    "& svg": {
      color: "#FFF",
    },
  },
  "&.disabled .playback-overlay-button__button": {
    cursor: "default",
    "&:hover": {
      backgroundColor: theme.palette.transparent.white[30],
    },
  },
}));

type PlaybackOverlayButtonProps = Omit<ButtonProps, "children"> & {
  icon: PlaybackOverlayButtonIcon;
  loadDuration?: number;
};

const PlaybackOverlayButton = ({
  icon,
  loadDuration,
  onClick,
  ...props
}: PlaybackOverlayButtonProps) => {
  const Icon = SVG_BY_ICON[icon];

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onClick?.(e);
  };

  const button = (
    <StyledButton
      {...props}
      onClick={handleClick}
      className={onClick ? undefined : "disabled"}
    >
      <Icon />
    </StyledButton>
  );

  if (loadDuration) {
    return <LoadWrapper duration={loadDuration}>{button}</LoadWrapper>;
  }

  return button;
};

export default PlaybackOverlayButton;
